// pages/TopicList.js
import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useLocation, Link } from 'react-router-dom';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';
import AuxiliaryFolderCards from '../components/AuxiliaryFolderCards';

const ALLOWED_DOMAINS = new Set(['toshba', 'talmud']);
const DEFAULT_DOMAIN = 'toshba';

export default function TopicList() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // domain / chapter from URL
  const rawDomain = (searchParams.get('domain') || localStorage.getItem('domain') || DEFAULT_DOMAIN).toLowerCase();
  const domain = ALLOWED_DOMAINS.has(rawDomain) ? rawDomain : DEFAULT_DOMAIN;
  const chapter = searchParams.get('chapter') || '';

  // tractate/chapter display names
  const [tractateName, setTractateName] = useState(() => location.state?.tractateName || '');
  const [chapterTitle, setChapterTitle] = useState('');

  // data
  const [topics, setTopics] = useState([]);
  const [chapterFiles, setChapterFiles] = useState([]);
  const [folders, setFolders] = useState([]);

  // loading/errors
  const [loading, setLoading] = useState(true);
  const [nameLoading, setNameLoading] = useState(!location.state?.tractateName);
  const [cfLoading, setCfLoading] = useState(Boolean(chapter)); // load only if chapter given
  const [error, setError] = useState('');
  const [cfError, setCfError] = useState('');

  // Fetch topics (with filters)
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const qs = new URLSearchParams();
        if (domain)  qs.set('domain', domain);
        if (chapter) qs.set('chapter', chapter);

        const res = await fetchWithAuth(`/api/tractates/${id}/topics/?${qs.toString()}`);
        if (!res.ok) throw new Error('Failed to load topics');
        const data = await res.json();
        if (!ignore) setTopics(data);
      } catch {
        if (!ignore) setError('שגיאה בטעינת הסוגיות');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id, domain, chapter]);

  // If we didn't get the name via state, fetch the tractate to get it
  useEffect(() => {
    if (tractateName) { setNameLoading(false); return; }
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/tractates/${id}/`);
        if (!res.ok) throw new Error('failed');
        const data = await res.json();
        if (!ignore) setTractateName(data?.name || '');
      } catch {
        // silent → breadcrumb will show a generic label
      } finally {
        if (!ignore) setNameLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id, tractateName]);

  // Fetch the chapter list (filtered by domain) and pick the current one for label
  useEffect(() => {
    if (!chapter) { setChapterTitle(''); return; }
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/tractates/${id}/chapters/?domain=${encodeURIComponent(domain)}`);
        if (!res.ok) throw new Error('chapters failed');
        const data = await res.json();
        const chId = String(chapter);
        const match = (data || []).find(c => String(c.id) === chId);
        const title =
          match?.display?.trim()
          || match?.title?.trim()
          || (match?.number != null ? `פרק ${match.number}` : '');
        if (!ignore) setChapterTitle(title || '');
      } catch {
        if (!ignore) setChapterTitle('');
      }
    })();
    return () => { ignore = true; };
  }, [id, domain, chapter]);

  // Fetch chapter files (only when a specific chapter is selected)
  useEffect(() => {
    if (!chapter) { setChapterFiles([]); setCfLoading(false); setCfError(''); return; }
    let ignore = false;
    setCfLoading(true);
    setCfError('');
    (async () => {
      try {
        const qs = new URLSearchParams({ domain, chapter });
        const res = await fetchWithAuth(`/api/tractates/${id}/chapter_files/?${qs.toString()}`);
        if (!res.ok) throw new Error('chapter files failed');
        const data = await res.json();
        if (!ignore) setChapterFiles(data || []);
      } catch {
        if (!ignore) setCfError('שגיאה בטעינת חומרי העזר לפרק');
      } finally {
        if (!ignore) setCfLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id, domain, chapter]);

  // Fetch chapter-level auxiliary folders
  useEffect(() => {
    if (!chapter) { setFolders([]); return; }
    let ignore = false;
    (async () => {
      try {
        const qs = new URLSearchParams({
          tractate: String(id),
          chapter: String(chapter),
          domain,
        });
        const res = await fetchWithAuth(`/api/auxiliary-folders/?${qs}`);
        const data = res.ok ? await res.json() : [];
        if (!ignore) setFolders(data);
      } catch {
        if (!ignore) setFolders([]);
      }
    })();
    return () => { ignore = true; };
  }, [id, domain, chapter]);

  // Helpers
  const backToTractate = `/tractates/${id}?domain=${domain}`;
  const topicsTitle = chapterTitle ? `סוגיות — ${chapterTitle}` : 'סוגיות';

  const fileNameFromUrl = (url) => {
    try {
      const u = new URL(url, window.location.origin);
      return decodeURIComponent(u.pathname.split('/').pop() || 'קובץ');
    } catch {
      return 'קובץ';
    }
  };

  // Derive a human-friendly title from either file or url
const chapterFileTitle = (f) => {
  if (f?.title && f.title.trim()) return f.title.trim();
  if (f?.file) return fileNameFromUrl(f.file);
  if (f?.url)  {
    try {
      const u = new URL(f.url);
      const host = u.hostname.replace(/^www\./, '');
      const path = u.pathname.replace(/\/$/, '');
      return `${host}${path !== '/' ? path : ''}` || f.url;
    } catch {
      return f.url;
    }
  }
  return 'קישור';
};

// Optional: nicer download name when a file exists
const downloadNameForChapterFile = (f) => {
  const fallback = chapterFileTitle(f);
  try {
    const u = new URL(f?.file || '', window.location.origin);
    const base = decodeURIComponent(u.pathname.split('/').pop() || fallback);
    return base || fallback;
  } catch {
    return fallback;
  }
};

  function fallbackOnce(e, fallbackSrc) {
    const img = e.target;
    if (img.dataset.fallbackApplied) return;   // prevent infinite loop
    img.dataset.fallbackApplied = "true";
    img.src = fallbackSrc;
  }

  // ---- Domain-aware breadcrumb label + domain-preserving list link ----
  const domText = domain === 'talmud' ? 'תלמוד' : 'תושב"ע';
  const tractateCrumbLabel = tractateName ? `${tractateName} (${domText})` : 'מסכת';
  const tractatesHref = `/tractates?domain=${domain}`;

  return (
    <div className="container">
      <Breadcrumbs
        items={[
          { to: tractatesHref, label: 'מסכתות' },                          // preserve ?domain
          { to: backToTractate, label: tractateCrumbLabel },               // add (תושב"ע|תלמוד)
          { label: chapterTitle ? `סוגיות ${chapterTitle}` : 'סוגיות' },
        ]}
      />

      <div className="toolbar toolbar--center">
        <h2 style={{ marginInlineStart: 0 }}>
          {tractateName ? `${topicsTitle} — ${tractateName}` : topicsTitle}
        </h2>
      </div>

      {/* keep domain/chapter when going back; pass the name back too for consistency */}
      <p className="center" style={{ marginTop: '.5rem' }}>
        <Link to={backToTractate} state={{ tractateName }}>
          חזרה למסכת
        </Link>
      </p>

      {(loading || nameLoading) && <p className="muted">טוען…</p>}
      {error && <p style={{ color: 'crimson' }}>{error}</p>}

      {/* Topics grid */}
      {!loading && !error && topics.length === 0 && (
        <p className="muted">לא נמצאו סוגיות.</p>
      )}

      {!loading && !error && topics.length > 0 && (
        <div className="grid-autofit">
          {topics.map((t) => {
            const to = `/tractates/${id}/topics/${t.id}?domain=${domain}${chapter ? `&chapter=${encodeURIComponent(chapter)}` : ''}`;
            return (
              <article key={t.id} className="c-card">
                <Link to={to} state={{ tractateName }}>
                  <img
                    className="c-card__media"
                    src={t.image || `/assets/tractates/${id}/intro.png`}
                    alt={t.name}
                    loading="lazy"
                    onError={(e) => fallbackOnce(e, `/assets/cards/topicList.png`)}
                  />
                </Link>
                <div className="c-card__body">
                  <h3 className="c-card__title">
                    <Link to={to} state={{ tractateName }}>
                      {t.name}
                    </Link>
                  </h3>
                  <p className="c-card__meta">לחצו כדי להיכנס לסוגיה</p>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {chapter && folders.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <p className="muted" style={{ marginBottom: '.5rem' }}>תיקיות עזר</p>
          <AuxiliaryFolderCards
            folders={folders}
            state={{ tractateName, chapterTitle }}
            buildTo={(folder) =>
              `/tractates/${id}/auxiliary/${folder.id}?domain=${domain}&chapter=${encodeURIComponent(chapter)}`
            }
          />
        </div>
      )}

      {/* --- Extra resources (chapter files) --- */}
      {chapter && (
        <div className="stack-3" style={{ marginTop: '2rem' }}>
          <details className="accordion">
            <summary
              style={{
                cursor: 'pointer',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '.75rem',
              }}
            >
              <span className="acc-title">
                חומרי עזר נוספים{chapterTitle ? ` — ${chapterTitle}` : ''}
              </span>
              <span className="acc-spacer" />
              {!cfLoading && !cfError ? (
                <span className="muted" style={{ fontWeight: 500 }}>
                  {chapterFiles.length} פריטים
                </span>
              ) : null}
            </summary>

            <div className="accordion__content">
              {cfLoading && <p className="muted">טוען חומרי עזר…</p>}
              {cfError && <p style={{ color: 'crimson' }}>{cfError}</p>}

              {!cfLoading && !cfError && chapterFiles.length === 0 && (
                <p className="muted">אין חומרי עזר נוספים לפרק זה.</p>
              )}

              {!cfLoading && !cfError && chapterFiles.length > 0 && (
                <div className="cf-table">
                  {chapterFiles.map((f) => {
                    const title = chapterFileTitle(f);
                    const isLink = !!f.url && !f.file;
                    const hasFile = !!f.file;
                    const downloadName = hasFile ? downloadNameForChapterFile(f) : '';

                    return (
                      <div key={f.id} className="cf-row">
                        <div className="cf-main">
                          <div className="cf-title">{title}</div>
                          {f.author ? (
                            <div className="cf-author muted">מאת {f.author}</div>
                          ) : null}
                          {isLink ? (
                            <div className="muted" style={{ fontSize: '.85rem' }}>קישור חיצוני</div>
                          ) : null}
                        </div>

                        <div className="cf-actions" style={{ display: 'flex', gap: '.5rem' }}>
                          {/* Links: open only */}
                          {isLink && (
                            <a
                              className="btn btn--sm"
                              href={f.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`פתיחת קישור: ${title}`}
                            >
                              פתיחה
                            </a>
                          )}

                          {/* Files: download only */}
                          {hasFile && (
                            <a
                              className="btn btn--sm"
                              href={f.file}
                              download={downloadName}
                              aria-label={`הורדת ${title}`}
                            >
                              הורדה
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </details>
        </div>
      )}



    </div>
  );
}

